/* eslint-disable max-len */
import comm from 'Modules/comm'
import * as endpoint from 'Modules/constants'
import pipeline from './pipeline'

/** common to board, screens, views */
const createCategoryMethods = (parentType) => {
  return {
    create: payload => comm.post(`${endpoint.ADD_CATEGORY_ENDPOINT}/${parentType}`, payload),
    update: ({ id, name }) => comm.patch(`${endpoint.EDIT_CATEGORY_ENDPOINT}/${parentType}/${encodeURIComponent(id)}`, { name }),
    delete: ({ id }) => comm.delete(`${endpoint.REMOVE_CATEGORY_ENDPOINT}/${parentType}/${id}`),
    addItem: ({ categoryid, ids }) => comm.patch(`${endpoint.ADD_TO_CATEGORY_ENDPOINT}/${parentType}`, { categoryid, ids }),
    removeItem: ({ categoryid, id }) => comm.patch(`${endpoint.REMOVE_FROM_CATEGORY_ENDPOINT}/${parentType}`, { id, categoryid })
  }
}
/** comon to board, screens, views */
const createFavoriteMethods = (parentType) => {
  return {
    update: ({ favorites }) => comm.patch(`${endpoint.FAVORITES_ENDPOINT}/${parentType}`, { favorites })
  }
}
/** comon to board, screens, views */
const createListMethod = parentType => () => comm.get(`${endpoint.FRAME_CONTENT_ENDPOINT}/${parentType}`)

class LogdnaApi {
  // Expose the raw fetch class
  // the difference between this and normal fetch is that
  // it contains the correct headers and csrf etc.
  fetch = comm

  get account() {
    return {
      change_owner: ({ account, payload }) => comm.post(`${endpoint.CHANGE_OWNER_ENDPOINT}/${account}`, payload),
      change_plan: ({ plan }) => comm.put(endpoint.CHANGE_PLAN_ENDPOINT, { plan }),
      extend_trial: () => comm.patch(endpoint.EXTEND_TRIAL_PLAN_ENDPOINT),
      reauth: ({ password }) => comm.post(endpoint.REAUTH_ENDPOINT, { password }),
      billing: {
        pay_outstanding: ({ token, address }) => comm.post(endpoint.PAY_OUTSTANDING_ENDPOINT, { token: token.id, card: token.card.id, address }),
        complete_checkout: payload => comm.post(endpoint.COMPLETE_CHECKOUT, payload),
        begin_checkout: ({ plan, product }) =>comm.get(`${endpoint.BEGIN_CHECKOUT}/${plan}?product=${product}`),
        add_payment: ({ token, card }) => comm.post(endpoint.ADD_PAYMENT, { token, card }),
        invoice: ({ invoiceno }) => comm.get(`${endpoint.GET_INVOICE_HTML}/${invoiceno}`),
        detail: {
          read: () => comm.get(endpoint.BILLING_DETAIL_ENDPOINT)
        },
        settings: {
          update: payload => comm.post(endpoint.SAVE_BILLING_SETTINGS, payload)
        }
      },
      config: {
        read: () => comm.get(endpoint.CONFIG_STATS_ENDPOINT),
        export: ({ account, payload }) => comm.post(`${endpoint.EXPORT_CONFIG_ENDPOINT}/${account}`, payload)
      },
      email: {
        verify: ({ token }) => comm.get(`${endpoint.VERIFY_EMAIL_URL}/${token}`),
        resend: () => comm.put(endpoint.RESEND_VERIFICATION_URL)
      },
      otp: {
        read: () => comm.post(endpoint.USER_PREFS_OTP_STATE_ENDPOINT),
        enable: () => comm.post(endpoint.USER_PREFS_OTP_ENDPOINT),
        disable: ({ password }) => comm.delete(endpoint.USER_PREFS_OTP_ENDPOINT, { password }),
        backup_codes: ({ password }) => comm.post(endpoint.USER_PREFS_OTP_STATE_ENDPOINT, { password }),
        verify: ({ token }) => comm.post(endpoint.USER_PREFS_OTP_ACTIVATE_ENDPOINT, { token })
      },
      password: {
        create: ({ token, email, password }) => comm.post(`${endpoint.ACCOUNT_SETUP_PASSWORD_ENDPOINT}/${token}`, { email, password }),
        update: ({ email, token, password }) => comm.post(endpoint.CHANGE_PASSWORD_ENDPOINT, { email, token, password }),
        forgot: ({ email }) => comm.post(endpoint.FORGOT_PASSWORD_ENDPOINT, { email })
      },
      prefs: {
        update: ({ prefs }) => comm.patch(endpoint.USER_PREFS_SET_ENDPOINT, prefs),
        change_password: ({ currentPassword, newPassword }) => comm.put(endpoint.USER_PREFS_CHANGE_PASSWORD_ENDPOINT, { currentPassword, newPassword })
      },
      profile: {
        read: () => comm.get(endpoint.PROFILE_DETAILS_ENDPOINT),
        update: ({ accountid, settings }) => comm.put(`${endpoint.UPDATE_PROFILE_ENDPOINT}/${accountid}`, settings),
        delete: ({ account, accountIdConfirmation }) => comm.delete(`${endpoint.DEACTIVATE_ACCOUNT_ENDPOINT}/${account}?validation=${encodeURIComponent(accountIdConfirmation)}`),
        ingestionKeys: {
          put: (payload, options) => comm.put(endpoint.INGESTION_KEY_MANAGE_ENDPOINT),
          patch: ({ key }, payload, options) => comm.patch(`${endpoint.INGESTION_KEY_MANAGE_ENDPOINT}/${key}`, payload),
          delete: ({ key }) => comm.delete(`${endpoint.INGESTION_KEY_MANAGE_ENDPOINT}/${key}`)
        },
        serviceKeys: {
          put: (payload, options) => comm.put(endpoint.SERVICE_KEY_MANAGE_ENDPOINT, payload, options),
          patch: ({ key }, payload, options) => comm.patch(`${endpoint.SERVICE_KEY_MANAGE_ENDPOINT}/${key}`, payload, options),
          delete: ({ key }) => comm.delete(`${endpoint.SERVICE_KEY_MANAGE_ENDPOINT}/${key}`)
        }
      },
      products: {
        patch: (products) => comm.patch(endpoint.UPDATE_PRODUCTS_ENDPOINT, { products })
      }
    }
  }

  get alerts() {
    return {
      list: () => comm.get(endpoint.LIST_ALERTS_ENDPOINT),
      unmute: ({ alertid }) => comm.put(endpoint.UNMUTE_ALERT, { alertid }),
      unmute_ext: ({ alertid, account, token }) => comm.put(`${endpoint.UNMUTE_ALERT_MOSTLY_THE_SAME}/${token}`, { alertid, account }),
      test_alert: ({ viewRef, alert }) => {
        var url = endpoint.TEST_ALERT
        if (viewRef) {
          url += '/' + viewRef
        }

        return comm.post(url, { alert })
      },
      preset: {
        delete: ({ presetid }) => comm.delete(`${endpoint.DELETE_ALERT_PRESET_ENDPOINT}/${presetid}`),
        update: ({ alert }) => comm.put(`${endpoint.SAVE_ALERT_PRESET_ENDPOINT}${alert.id ? '/' + alert.id : ''}`, alert)
      }
    }
  }

  get archive() {
    return {
      create: ({ active, config }) =>comm.post(`${endpoint.SAVE_ARCHIVING_ENDPOINT}/${active}`, config),
      read: () =>comm.get(endpoint.GET_ARCHIVING_STATE_ENDPOINT),
      delete: () =>comm.delete(endpoint.DISABLE_ARCHIVING_ENDPOINT)
    }
  }

  get boards() {
    return {
      render: (payload, options) => comm.post(endpoint.RENDER_GRAPH_ENDPOINT, payload, options),
      list: createListMethod('boards'),
      duplicate: ({ boardid, payload }) => comm.put(`${endpoint.DUPLICATE_BOARD_ENDPOINT}/${boardid}`, payload),
      update: ({ boardid, settings }) => {
        let url = `${endpoint.SAVE_BOARD_SETTINGS_ENDPOINT}/${boardid}`
        let payload = settings

        if (typeof settings.graph === 'object') { // this is a graph specific persist
          url = `${url}?gid=${settings.graph.id}`
          payload = settings.graph
        }

        return comm.patch(url, payload)

      },
      delete: ({ boardid }) => comm.delete(`${endpoint.GRAPHS_ROOT}/${boardid}`),
      graph: {
        create: ({ payload, order, index }) => {
          var url = endpoint.SAVE_GRAPH_ENDPOINT
          if (payload.boardid) {
            url += '/' + payload.boardid
          }
          if (order?.length && index) {
            url += '?order=' + order.join(',') + '&index=' + payload.index
          }
          return comm.post(url, payload)
        },
        read: ({ boardid }) => comm.get(`${endpoint.GET_GRAPH_DATA_ENDPOINT}/${boardid}`),
        delete: ({ payload, boardid }) => comm.delete(`${endpoint.REMOVE_GRAPH_ENDPOINT}/${boardid}?gid=${payload.graphId}`)
      },
      category: { ...createCategoryMethods('boards') },
      favorites: { ...createFavoriteMethods('boards') }
    }
  }

  get enterprise() {
    return {
      get: ({ id }) => comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${id}`),
      patch: ({ id, payload }) => comm.patch(`${endpoint.ENTERPRISE_ENDPOINT}/${id}`, payload),
      put: ({ id, payload }) => comm.put(endpoint.ENTERPRISE_SAML_CONFIG_ENDPOINT(id), payload),
      accounts: {
        list: ({ enterpriseId }) => {
          return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/accounts`)
        },
        get({ enterpriseId, accountId, serviceKey }) {
          return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/accounts/${accountId}`, { headers: { ServiceKey: serviceKey } })
        },
        create: ({ enterpriseId, payload }) => {
          return comm.post(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/accounts`, payload)
        },
        deactivate: ({ enterpriseId, accountId, accountIdConfirmation }) => {
          return comm.delete(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/accounts/${accountId}?validation=${encodeURIComponent(accountIdConfirmation)}`)
        }
      },
      users: {
        list: ({ enterpriseId }) => comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/users`),
        get: ({ enterpriseId }) => comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/users`)
      },
      usage_stats: ({ enterpriseId }) => {
        return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/usage-info`)
      },
      accounts_usage: ({ enterpriseId, startDate, endDate }) => {
        return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/usage-per-account`, { params: { startDate, endDate } })
      },
      accounts_feature_usage: ({ enterpriseId }) => {
        return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/accounts-feature-usage`)
      },
      billing_methodology: ({ enterpriseId }) => {
        return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/billing-methodology`)
      },
      analytics: {
        sources_breakdown: ({ enterpriseId, startDate, retentionPeriod, endDate, download }) => {
          return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/analytics/sources-breakdown`, { params: { startDate, endDate, retentionPeriod, download } })
        },
        trends: ({ enterpriseId, type, sources, retentionPeriod, startDate, endDate }) => {
          return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/analytics/trends`, { params: { startDate, endDate, sources, type, retentionPeriod } })
        },
        history: ({ enterpriseId, startDate, endDate }) => {
          return comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/analytics/history`, { params: { startDate, endDate } })
        }
      },
      contracts: {
        list: ({ enterpriseId }) => comm.get(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/contracts`)
      },
      serviceKeys: {
        create: ({ enterpriseId }) => comm.post(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/service-keys/create`, { enterpriseId }),
        delete: ({ enterpriseId, serviceKeyId }) => comm.delete(`${endpoint.ENTERPRISE_ENDPOINT}/${enterpriseId}/service-keys/delete/${serviceKeyId}`)
      }
    }
  }

  get fields() {
    return {
      list: ({ accountid }) => comm.get(`${endpoint.AVAILABLE_FIELDS_ENDPOINT}?account=${accountid}`),
      distribution: ({ field }) => comm.post(`${endpoint.VALUE_DISTRIBUTION_ENDPOINT}/${field}`),
      extract: (payload, options) => comm.post(`${endpoint.EXTRACT_FIELDS_ENDPOINT}`, payload, options)
    }
  }

  get filters() {
    return {
      list: ({ federated }) => comm.get(`${endpoint.FILTERS_ENDPOINT}${federated ? '?federated=true' : ''}`),
      search: ({ category, query, options }) => {
        let url = `${endpoint.FILTERS_ENDPOINT}/${category}?q=${query}`
        if (!options.save && options.groups === false) {
          url += '&g=0'
        }
        return comm.get(url)
      },
      group: {
        create: ({ payload }) => comm.post(endpoint.SAVE_GROUP_ENDPOINT, payload),
        delete: ({ groupid }) => comm.delete(`${endpoint.DELETE_GROUP_ENDPOINT}/${groupid}`)
      }
    }
  }

  get graph() {
    return {
      read: payload => comm.post(endpoint.RENDER_GRAPH_ENDPOINT, payload)
    }
  }

  get index_rate_alerts() {
    return {
      list: () => comm.get(endpoint.CRUD_INDEX_RATE_ALERT_CONFIG_ENDPOINT),
      update: ({ payload }) => comm.patch(endpoint.CRUD_INDEX_RATE_ALERT_CONFIG_ENDPOINT, payload)
    }
  }

  get ingestion() {
    return {
      list: () => comm.get(endpoint.GET_CONTROLS_ENDPOINT),
      update: ({ value }) => comm.put(endpoint.SAVE_CONTROLS_ENDPOINT, value),
      rules: {
        create: rule => comm.put(endpoint.UPDATE_RULE_ENDPOINT, rule),
        list: () => comm.get(endpoint.LIST_RULES_ENDPOINT),
        toggle: ({ id, active }) => comm.put(`${endpoint.TOGGLE_RULE_ENDPOINT}/${id}`, { active }),
        delete: ({ id }) => comm.delete(`${endpoint.UPDATE_RULE_ENDPOINT}/${id}`),
        update: ({ payload }) => comm.put(`${endpoint.UPDATE_RULE_ENDPOINT}/${payload.id}`, payload.rule)

      }
    }
  }

  get integrations() {
    return {
      list: () => comm.get(endpoint.GET_INTEGRATIONS_ENDPOINT),
      delete: ({ integration, serviceKey }) => comm.delete(`${endpoint.INTEGRATIONS_ENDPOINT}/${integration}/${encodeURIComponent(serviceKey)}`),
      usage: ({ integration }) => comm.get(`${endpoint.INTEGRATION_USAGE_ENDPOINT}/${integration}`),
      github: {
        update: ({ account, payload }) => comm.post(`${endpoint.INTEGRATION_UPDATE_URL}/github?account=${account}`, payload),
        auth: () => comm.get(`${endpoint.AUTH_STATUS_URL}/github`),
        template: ({ page, perPage }) => comm.get(`${endpoint.FIELDSET_TEMPLATE_URL}/github?page=${page}&per_page=${perPage}`)
      }
    }
  }

  get lines() {
    return {
      search: (options) => comm.get(`${endpoint.SEARCH_ENDPOINT}`, options),
      read: ({ lineId }) => comm.get(`${endpoint.LINE_ENDPOINT}/${lineId}`),
      query: ({ search }) => comm.get(`${endpoint.SEARCH_LINES_ENDPOINT}?${search}`),
      export: payload => comm.post(endpoint.EXPORT_LINES_ENDPOINT, payload),
      context: ({ ts, id, tz, line }) => comm.post(`${endpoint.LINE_CONTEXT_ENDPOINT}/${ts}.${id}${!tz ? '?tz=' + tz : ''}`, line),
      by_context: ({ ts, key, pivot }) => comm.post(`${endpoint.LINES_BY_CONTEXT_ENDPOINT}/${ts}.${key}?type=l`, { pivot }),
      kube_resource: ({ ts, qs }) => comm.get(`${endpoint.K8S_RESOURCE_ENDPOINT}/${ts}?${qs}`)
    }
  }

  get histogram() {
    return {
      get: (options) => comm.get(`${endpoint.HISTOGRAM_ENDPOINT}`, options)
    }
  }

  get notifications() {
    return {
      delete: (payload) => comm.delete(endpoint.NOTIFICATIONS, payload)
    }
  }

  get orgs() {
    return {
      list: () => comm.get(endpoint.GET_ORGANIZATIONS_ENDPOINT),
      create: ({ payload }) => comm.put(endpoint.CREATE_ORG_ENDPOINT, payload),
      addAccount: ({ account }) => comm.put(`${endpoint.JOIN_ORG_ENDPOINT}/${account}`),
      removeAccount: ({ account }) => comm.delete(`${endpoint.LEAVE_ORG_ENDPOINT}/${account}`),
      activate: ({ account }) => comm.put(`${endpoint.ACTIVATE_ACCOUNT_ENDPOINT}/${account}`),
      whitelist: {
        create: ({ domain }) => comm.put(endpoint.WHITELIST_DOMAIN_MANAGE_ENDPOINT, { domain }),
        delete: ({ domain }) => comm.delete(`${endpoint.WHITELIST_DOMAIN_MANAGE_ENDPOINT}?domain=${encodeURIComponent(domain)}`)
      }
    }
  }

  get parsing() {
    return {
      list: () => comm.get(endpoint.GET_CUSTOM_PARSING_TEMPLATES_ENDPOINT),
      create: ({ payload }) => comm.post(`${endpoint.CREATE_PARSING_TEMPLATE_ENDPOINT}`, payload),
      update: ({ templateid, payload }) => comm.put(`${endpoint.EDIT_TEMPLATE_ENDPOINT}/${templateid}`, payload),
      delete: ({ templateid }) => comm.delete(`${endpoint.REMOVE_CUSTOM_PARSING_TEMPLATE_ENDPOINT}/${templateid}`),
      draft: ({ id }) => comm.post(endpoint.CREATE_TEMPLATE_DRAFT_ENDPOINT, { id }),
      activate: ({ templateid, payload }) => comm.put(`${endpoint.ACTIVATE_TEMPLATE_ENDPOINT}/${templateid}`, payload),
      prioritize: ({ ids }) => comm.patch(endpoint.PRIORITIZE_TEMPLATES_ENDPOINT, { ids })

    }
  }

  get pipelineProxy() {
    return pipeline
  }

  get provision() {
    return {
      syslog: () => comm.post(endpoint.PROVISION_SYSLOG_ENDPOINT)
    }
  }

  get quotas() {
    return {
      create: ({ rule }) => comm.put(endpoint.CRUD_USAGE_QUOTAS_RULE_ENDPOINT, rule),
      update: ({ type, id, payload }) => comm.patch(`${endpoint.CRUD_USAGE_QUOTAS_RULE_ENDPOINT}/${type}/${id}`, payload),
      delete: ({ type, id }) => comm.delete(`${endpoint.CRUD_USAGE_QUOTAS_RULE_ENDPOINT}/${type}/${id}`),
      toggle: ({ type, ruleId, value }) => comm.patch(`${endpoint.CRUD_USAGE_QUOTAS_RULE_ENDPOINT}/toggle/${type}/${ruleId}`, { active: value }),
      recipients: {
        update: ({ type, value }) => comm.put(`${endpoint.UPDATE_USAGE_QUOTAS_RECIPIENTS_ENDPOINT}/${type}`, value)
      },
      settings: {
        list: () => comm.get(endpoint.GET_USAGE_QUOTAS_ENDPOINT),
        active: ({ active }) => comm.patch(endpoint.TOGGLE_USAGE_QUOTAS_ENDPOINT, { active }),
        update: ({ type, payload }) => comm.put(`${endpoint.UPDATE_USAGE_QUOTAS_ENDPOINT}/${type}`, payload)
      }
    }
  }

  get rbac() {
    return {
      list: () => comm.get(endpoint.GET_RBAC_STATE_ENDPOINT),
      create: ({ payload }) => comm.post(endpoint.ADD_RBAC_GROUP_ENDPOINT, payload),
      update: ({ payload }) => comm.put(endpoint.EDIT_RBAC_GROUP_ENDPOINT, payload),
      delete: ({ id }) => comm.delete(`${endpoint.REMOVE_RBAC_GROUP_ENDPOINT}/${id}`)
    }
  }

  get rehydrations() {
    return {
      list: () => comm.get(endpoint.REHYDRATIONS_ENDPOINT),
      create: ({ payload }) => comm.post(endpoint.REHYDRATIONS_ENDPOINT, payload),
      retry: ({ id }) => comm.post(`${endpoint.REHYDRATIONS_RETRY_ENDPOINT}/${id}`),
      archive: {
        list: (options) => comm.get(endpoint.REHYDRATIONS_ARCHIVES_ENDPOINT, options)
      }
    }
  }

  get screens() {
    return {
      render: (payload, options) => comm.post(endpoint.RENDER_WIDGET_ENDPOINT, payload, options),
      list: createListMethod('screens'),
      create: ({ payload }) => comm.post(endpoint.SAVE_SCREEN_ENDPOINT, payload),
      read: ({ screenid }) => comm.get(`${endpoint.GET_SCREEN_DATA_ENDPOINT}/${screenid}`),
      update: ({ payload }) => comm.post(`${endpoint.SAVE_SCREEN_ENDPOINT}/${payload.screenid}`, payload),
      delete: ({ screenid }) => comm.delete(`${endpoint.DELETE_SCREEN_ENDPOINT}/${screenid}`),
      duplicate: ({ screenid, payload }) => comm.put(`${endpoint.DUPLICATE_SCREEN_ENDPOINT}/${screenid}`, payload),
      favorites: { ...createFavoriteMethods('screens') },
      category: { ...createCategoryMethods('screens') }
    }
  }

  get searches() {
    return {
      list: () => comm.get(endpoint.RECENT_SEARCHES_ENDPOINT)
    }
  }

  get teams() {
    return {
      member: {
        list: () => comm.get(endpoint.GET_TEAM_MEMBERS_ENDPOINT),
        create: ({ member }) => comm.post(endpoint.ADD_MEMBER_ENDPOINT, member),
        delete: ({ member }) => comm.delete(`${endpoint.REMOVE_MEMBER_ENDPOINT}/${encodeURIComponent(member)}`),
        accept: ({ token }) => comm.post(`${endpoint.ACCEPT_JOIN_REQUEST_ENDPOINT}/${token}`),
        reject: ({ token }) => comm.post(`${endpoint.REJECT_JOIN_REQUEST_ENDPOINT}/${token}`),
        update: ({ member, newRole }) => comm.post(`${endpoint.MODIFY_PERMISSIONS_ENDPOINT}/${member}`, { newRole }),
        modify_group: ({ email, groups }) => comm.post(`${endpoint.MODIFY_GROUPS_LIST_ENDPOINT}/${email}`, { groups })
      },
      access: {
        saml: ({ account, config }) => comm.put(`${endpoint.SAML_CONFIG_ENDPOINT}/${account}`, config)
      }
    }
  }

  get template_library() {
    return {
      list: ({ accountid }) => comm.get(`${endpoint.TEMPLATE_LIBRARY_ENDPOINT}?account=${accountid}`),
      available: () => comm.get(endpoint.TEMPLATE_LIBRARY_ENDPOINT_DETECT),
      create: ({ template, account }) => comm.post(endpoint.TEMPLATE_LIBRARY_ENDPOINT, { template, account }),
      delete: (payload) => comm.delete(endpoint.TEMPLATE_LIBRARY_ENDPOINT, payload)
    }
  }

  get usage() {
    return {
      data: () => comm.get(endpoint.USAGE_DATA_ENDPOINT),
      history: ({ startDate, endDate }) => comm.get(`${endpoint.USAGE_HISTORY_ENDPOINT}?from=${startDate || ''}&to=${endDate || ''}`),
      flow_historical: () => comm.get(endpoint.FLOW_RATE_HISTORICAL_ENDPOINT),
      flow_hour: () => comm.get(endpoint.FLOW_RATE_CURRENT_HOUR_ENDPOINT),
      breakdown: ({ retentionPeriod, startDate, endDate }) => comm.get(`${endpoint.SOURCES_BREAKDOWN_ENDPOINT}/?retentionperiod=${retentionPeriod || ''}&from=${startDate || ''}&to=${endDate || ''}`),
      trends: params => comm.get(endpoint.TRENDS_ENDPOINT, params)
    }
  }

  get variable_retention() {
    return {
      list: () => comm.get(endpoint.VARIABLE_RETENTION_ENDPOINT),
      create: ({ rule }) => comm.post(endpoint.VARIABLE_RETENTION_ENDPOINT, { rule }),
      update: ({ rule }) => comm.put(`${endpoint.VARIABLE_RETENTION_ENDPOINT}/${rule.retentionid}`, { rule }),
      delete: ({ id }) => comm.delete(`${endpoint.VARIABLE_RETENTION_ENDPOINT}/${id}`),
      prioritize: ({ rules }) => comm.put(endpoint.VARIABLE_RETENTION_ENDPOINT, { rules })
    }
  }

  get views() {
    return {
      list: createListMethod('views'),
      create: ({ payload }) => comm.post(endpoint.SAVE_VIEW_ENDPOINT, payload),
      read: ({ viewid }) => comm.get(`${endpoint.GET_VIEW_DATA_ENDPOINT}/${viewid}`),
      update: ({ payload }) => comm.put(endpoint.SAVE_VIEW_ENDPOINT, payload),
      delete: ({ id, accountId }) => comm.post(endpoint.DELETE_VIEW_ENDPOINT, { id, accountId }),
      alert: {
        update: ({ alert }) => comm.put(`${endpoint.SAVE_VIEW_SPECIFIC_ALERT_ENDPOINT}/${alert.id}`, alert),
        delete: ({ viewid }) => comm.delete(`${endpoint.DELETE_VIEW_SPECIFIC_ALERT_ENDPOINT}/${viewid}`)
      },
      category: { ...createCategoryMethods('views') },
      favorites: { ...createFavoriteMethods('views') },
      props: {
        update: ({ viewid, payload }) => comm.put(`${endpoint.SAVE_VIEW_PROPS_ENDPOINT}/${viewid}`, payload)
      },
      viewer_settings: () => comm.get(endpoint.GET_VIEWER_SETTINGS_ENDPOINT)
    }
  }

  get cms() {
    return {
      get: () => comm.get(endpoint.EXTERNAL_CMS)
    }
  }

  get authProviderProxy() {
    return {
      serviceAccount: {
        get: () => comm.get(`${endpoint.AUTH_PROVIDER_PROXY}/service-account`),
        post: (payload) => comm.post(`${endpoint.AUTH_PROVIDER_PROXY}/service-account`, payload),
        put: (id, payload) => comm.put(`${endpoint.AUTH_PROVIDER_PROXY}/service-account/${id}`, payload),
        delete: (id) => comm.delete(`${endpoint.AUTH_PROVIDER_PROXY}/service-account/${id}`)
      }
    }
  }

  get pipeline() {
    return {
      restoration: {
        post: (payload) => comm.post('/account/pipeline/create-restoration', payload),
        get: ({ id }) => comm.get(`/account/pipeline/restoration?id=${id}`),
        list: (payload) => comm.get('/account/pipeline/restorations', payload)
      },
      ingestion_router: {
        get: () => comm.get('/account/pipeline/ingestion-router'),
        post: () => comm.post('/account/pipeline/ingestion-router')
      }
    }
  }

  get signup() {
    return {
      post: (payload, options) => comm.post('/ext/account-signup', payload, options)
    }
  }
}

const LogdnaApiInstance = new LogdnaApi()
export default LogdnaApiInstance

